import type { Permissao } from '@mymaxx/shared';
import {
  Activity,
  Building2,
  ChartColumn,
  HardHat,
  Headset,
  ClipboardList,
  FileLock2,
  MessageSquareText,
  MessagesSquare,
  Workflow,
  Home,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { authApi } from '../lib/api';
import { useAvisoNoComputador } from '../lib/push';
import { useSinalDePresenca } from '../lib/presenca';
import { useSessao } from '../lib/sessao';
import { SinoAlertas } from '../alertas/SinoAlertas';
import { MenuPerfil } from '../atendimento/MenuPerfil';
import { useSussurros } from '../lib/sussurros';
import { ProvedorInterno, useNaoLidasInternas } from '../lib/interno-tempo-real';
import { ProvedorTempoReal } from '../lib/tempo-real';
import { PainelSussurro } from '../supervisao/PainelSussurro';

interface ItemMenu {
  para: string;
  rotulo: string;
  Icone: typeof Home;
  /** Sem permissão, o item nem aparece (a API também barra). */
  permissao?: Permissao;
}

const MENU: ItemMenu[] = [
  { para: '/', rotulo: 'Início', Icone: Home },
  { para: '/atendimentos', rotulo: 'Atendimentos', Icone: Headset, permissao: 'conversas.atender' },
  // Sem permissão: conversar com a equipe é o básico de quem trabalha aqui, não um poder.
  { para: '/interno', rotulo: 'Conversas', Icone: MessagesSquare },
  { para: '/monitor', rotulo: 'Monitor', Icone: Activity, permissao: 'conversas.ver_setor' },
  { para: '/setores', rotulo: 'Setores', Icone: Building2, permissao: 'setores.gerir' },
  { para: '/usuarios', rotulo: 'Usuários', Icone: Users, permissao: 'usuarios.gerir' },
  { para: '/tecnicos', rotulo: 'Técnicos', Icone: HardHat, permissao: 'usuarios.gerir' },
  { para: '/perfis', rotulo: 'Perfis e permissões', Icone: ShieldCheck, permissao: 'perfis.gerir' },
  {
    para: '/atendimento',
    rotulo: 'Motivos e respostas',
    Icone: MessageSquareText,
    permissao: 'mensagens_rapidas.gerir',
  },
  { para: '/fluxos', rotulo: 'Fluxos', Icone: Workflow, permissao: 'fluxos.editar' },
  { para: '/relatorios', rotulo: 'Relatórios', Icone: ChartColumn, permissao: 'relatorios.setor' },
  { para: '/auditoria', rotulo: 'Auditoria', Icone: ClipboardList, permissao: 'auditoria.ver' },
  {
    para: '/privacidade',
    rotulo: 'Privacidade',
    Icone: FileLock2,
    permissao: 'configuracoes.gerir',
  },
];

/** Moldura das telas autenticadas: menu lateral filtrado por permissão e cabeçalho com o usuário. */
export function Layout() {
  const { usuario, limpar } = useSessao();
  const navegar = useNavigate();
  const itens = MENU.filter((i) => !i.permissao || usuario?.permissoes.includes(i.permissao));

  useSinalDePresenca();
  useAvisoNoComputador();

  async function sair() {
    await authApi.logout().catch(() => undefined);
    limpar();
    useSussurros.getState().limpar();
    navegar('/login', { replace: true });
  }

  return (
    <ProvedorTempoReal>
      {/* A conversa interna fica conectada enquanto a pessoa está logada, e não só quando
          a tela dela está aberta: é essa conexão que diz à equipe que ela está no
          MyMAXX, e é ela que faz o contador do menu acender em qualquer tela. */}
      <ProvedorInterno>
        {/* A moldura ocupa exatamente a altura da janela e cada parte rola por dentro; assim
        uma tela cheia (a caixa de entrada) sabe o espaço que tem em vez de adivinhar. */}
        <div className="min-h-screen md:flex md:h-screen md:overflow-hidden">
          <aside className="border-b border-neutral-200 md:h-screen md:w-60 md:shrink-0 md:overflow-y-auto md:border-r md:border-b-0 dark:border-neutral-800">
            <div className="px-4 py-4">
              <img src="/logo.png" alt="MyMAXX" className="h-7 w-auto" height={28} />
              <p className="text-xs text-neutral-500">Painel</p>
            </div>
            <nav className="flex gap-1 overflow-x-auto px-2 pb-2 md:flex-col md:overflow-visible">
              {itens.map(({ para, rotulo, Icone }) => (
                <NavLink
                  key={para}
                  to={para}
                  end={para === '/'}
                  className={({ isActive }) =>
                    `flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                      isActive
                        ? 'bg-marca-50 font-medium text-marca-800 dark:bg-marca-950 dark:text-marca-100'
                        : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                    }`
                  }
                >
                  <Icone size={16} /> {rotulo}
                  {para === '/interno' && <ContadorInterno />}
                </NavLink>
              ))}
            </nav>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <header className="flex items-center justify-end gap-2 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
              {/* O sino acompanha quem navega: o alerta é sobre o setor, não sobre a tela. */}
              <SinoAlertas />
              {/* Foto, senha, 2FA e sessões vivem atrás do retrato, igual à estação. */}
              <div className="w-56">
                <MenuPerfil aoSair={() => void sair()} />
              </div>
            </header>
            <main className="min-h-0 min-w-0 flex-1 overflow-y-auto px-4 py-6">
              <Outlet />
            </main>
          </div>

          {/* Fora do fluxo das telas: as janelas de sussurro seguem quem navega. */}
          <PainelSussurro />
        </div>
      </ProvedorInterno>
    </ProvedorTempoReal>
  );
}

/**
 * O número de mensagens internas esperando, ao lado do item do menu.
 *
 * Componente separado de propósito: assim só ele volta a desenhar quando chega mensagem,
 * em vez de a moldura inteira — e a moldura inteira inclui a tela que está aberta.
 */
function ContadorInterno() {
  const naoLidas = useNaoLidasInternas();
  if (naoLidas === 0) return null;
  return (
    <span className="ml-auto rounded-full bg-marca-600 px-1.5 text-[11px] font-medium text-white">
      {naoLidas > 99 ? '99+' : naoLidas}
    </span>
  );
}
